/**
 * WelcomeOnboardingModal — ekran powitalny dla nowych użytkowników APISAI.
 *
 * Pokazywany na Home (Centrum dowodzenia), gdy użytkownik nie ma jeszcze
 * utworzonej strony hodowli/firmy. Pomaga zrozumieć po co jest APISAI i wybrać
 * ścieżkę (Hodowca / Biznes). Addytywny — nie zmienia logiki kreatorów,
 * jedynie przekierowuje do właściwej ścieżki tworzenia strony.
 *
 * Dismiss zapisywany w localStorage (WELCOME_DISMISS_KEY) — bez rozbudowy backendu.
 * Wybór profilu używa istniejącego endpointu POST /api/profile/profile-type
 * (profil można później zmienić w ustawieniach — wybór niczego nie blokuje).
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, PawPrint, Briefcase, Info, Globe2, Clock, Sparkles, Loader2, ArrowRight } from 'lucide-react';
import { API } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';

export const WELCOME_DISMISS_KEY = 'apisai_welcome_onboarding_dismissed';

export default function WelcomeOnboardingModal({ onClose }) {
  const navigate = useNavigate();
  const { refreshUser } = useAuth() || {};
  const [busy, setBusy] = useState(null);

  // Blokada scrolla tła gdy modal otwarty
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const select = async (type) => {
    if (busy) return;
    setBusy(type);
    const route = type === 'hodowla' ? '/wizard/hodowla-v2' : '/wizard/firma-v2';
    try { localStorage.setItem(WELCOME_DISMISS_KEY, '1'); } catch (e) { /* noop */ }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/profile/profile-type`, { profile_type: type }, { headers: { Authorization: `Bearer ${token}` } });
      try { localStorage.setItem('apisai_profile_type', type); } catch (e) { /* noop */ }
      if (refreshUser) await refreshUser();
    } catch (e) {
      // Nie blokuj ścieżki, jeśli zapis preferencji się nie powiedzie — kreator i tak ustawia typ.
    }
    navigate(route);
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-stretch sm:items-center justify-center bg-black/55 sm:p-4 sm:backdrop-blur-sm animate-in fade-in duration-200"
      data-testid="welcome-onboarding-modal"
    >
      <div className="relative w-full sm:max-w-[760px] bg-white sm:rounded-3xl shadow-2xl min-h-full sm:min-h-0 sm:max-h-[94vh] overflow-y-auto px-5 py-7 sm:p-9 animate-in fade-in zoom-in-95 duration-200">
        {/* Zamknij */}
        <button
          type="button"
          onClick={onClose}
          data-testid="welcome-onboarding-close"
          aria-label="Zamknij"
          className="absolute right-4 top-4 w-9 h-9 rounded-full bg-[#F1F5F9] hover:bg-[#E2E8F0] flex items-center justify-center text-[#64748B] transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Nagłówek */}
        <div className="flex flex-col items-center text-center">
          <div className="relative mb-3">
            <Sparkles className="w-4 h-4 text-orange-300 absolute -left-3 -top-1.5" strokeWidth={2.2} />
            <img
              src="/logo-apisai.png"
              alt="APISAI"
              className="w-16 h-16 object-contain drop-shadow-[0_12px_26px_rgba(249,115,22,0.4)]"
            />
            <Sparkles className="w-3 h-3 text-orange-200 absolute -right-2.5 top-1" strokeWidth={2.2} />
          </div>
          <h2 className="text-[26px] sm:text-[30px] font-bold text-[#04202F] tracking-tight leading-tight">
            Witaj w APISAI 👋
          </h2>
          <p className="text-base sm:text-lg font-semibold text-[#04202F] mt-3 max-w-[440px] leading-snug">
            Stwórz stronę swojej hodowli, miotu, reproduktora lub firmy w kilka minut.
          </p>
          <p className="text-sm text-[#64748B] mt-3 max-w-[480px] leading-relaxed">
            APISAI pomaga Ci pokazać ofertę online, dodać ją do katalogu i zwiększyć zasięg.
            Przygotuj podstawowe informacje i zdjęcia — resztę przeprowadzimy krok po kroku.
          </p>
        </div>

        {/* Sekcja wyboru */}
        <h3 className="text-[15px] sm:text-base font-bold text-[#04202F] mt-7 mb-3.5">
          Wybierz, od czego zaczynamy
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Karta 1 — Hodowca */}
          <div
            className="rounded-2xl border border-[rgba(4,32,47,0.10)] bg-white p-5 sm:p-6 flex flex-col items-center text-center shadow-[0_2px_10px_rgba(4,32,47,0.05)]"
            data-testid="welcome-card-hodowca"
          >
            <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mb-3">
              <PawPrint className="w-6 h-6 text-orange-500" strokeWidth={1.9} />
            </div>
            <h4 className="text-lg font-bold text-[#04202F]">Jestem hodowcą</h4>
            <p className="text-sm text-[#475569] mt-2 leading-relaxed flex-1">
              Chcę stworzyć stronę hodowli, dodać suki, mioty lub reproduktory i zbierać zapytania od przyszłych właścicieli.
            </p>
            <button
              type="button"
              onClick={() => select('hodowla')}
              disabled={!!busy}
              data-testid="welcome-cta-hodowca"
              className="mt-5 w-full h-12 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-semibold text-sm inline-flex items-center justify-center gap-2 transition-colors shadow-[0_12px_28px_-10px_rgba(249,115,22,0.55)]"
            >
              {busy === 'hodowla' ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Wybieram profil hodowcy
              {busy !== 'hodowla' && <ArrowRight className="w-4 h-4" />}
            </button>
          </div>

          {/* Karta 2 — Biznes */}
          <div
            className="rounded-2xl border border-[rgba(4,32,47,0.10)] bg-white p-5 sm:p-6 flex flex-col items-center text-center shadow-[0_2px_10px_rgba(4,32,47,0.05)]"
            data-testid="welcome-card-biznes"
          >
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
              <Briefcase className="w-6 h-6 text-emerald-600" strokeWidth={1.9} />
            </div>
            <h4 className="text-lg font-bold text-[#04202F]">Prowadzę biznes</h4>
            <p className="text-sm text-[#475569] mt-2 leading-relaxed flex-1">
              Chcę stworzyć stronę firmy, pokazać usługi, ofertę, portfolio i zdobywać klientów z katalogu APISAI.
            </p>
            <button
              type="button"
              onClick={() => select('biznes')}
              disabled={!!busy}
              data-testid="welcome-cta-biznes"
              className="mt-5 w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold text-sm inline-flex items-center justify-center gap-2 transition-colors shadow-[0_12px_28px_-10px_rgba(5,150,105,0.55)]"
            >
              {busy === 'biznes' ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Wybieram profil biznesowy
              {busy !== 'biznes' && <ArrowRight className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Tekst uspokajający */}
        <div className="mt-4 flex items-center justify-center gap-2.5 rounded-xl bg-[#F6F7F9] border border-[rgba(4,32,47,0.06)] px-4 py-3 text-center">
          <Info className="w-4 h-4 text-[#94A3B8] flex-shrink-0" />
          <span className="text-sm text-[#64748B]">Możesz zmienić profil w każdej chwili w ustawieniach.</span>
        </div>

        {/* Stopka — katalog + badge */}
        <div className="mt-5 pt-4 border-t border-[rgba(4,32,47,0.08)] flex flex-col sm:flex-row items-center gap-3 sm:justify-between">
          <div className="flex items-center gap-2.5">
            <Globe2 className="w-5 h-5 text-[#94A3B8] flex-shrink-0" />
            <span className="text-xs text-[#64748B] leading-snug text-center sm:text-left">
              Katalog APISAI pomaga zwiększać zasięg i rozpoznawalność Twojej hodowli lub firmy.
            </span>
          </div>
          <div className="flex items-center gap-1.5 rounded-lg border border-[rgba(4,32,47,0.10)] bg-white px-3 py-2 flex-shrink-0">
            <Clock className="w-3.5 h-3.5 text-[#64748B]" />
            <span className="text-xs font-medium text-[#04202F] whitespace-nowrap">Start zajmuje około 5 minut</span>
          </div>
        </div>
      </div>
    </div>
  );
}
